import React, { useMemo, useState } from "react";
import { Badge, Card, IconGlyph, SectionLabel } from "./ProfileDashboardUI";

const formatCurrency = (amount = 0) => {
  const value = Number(amount);
  if (!Number.isFinite(value) || value === 0) return "Rs. 0";

  const absValue = Math.abs(value);
  if (absValue >= 10000000) return `Rs. ${(value / 10000000).toFixed(2)} Cr`;
  if (absValue >= 100000) return `Rs. ${(value / 100000).toFixed(2)} L`;
  if (absValue >= 1000) return `Rs. ${(value / 1000).toFixed(2)} K`;

  return `Rs. ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value)}`;
};

const getMaxMonthlyValue = (rows = []) =>
  Math.max(
    1,
    ...rows.flatMap((row) => [
      Number(row.savings || 0),
      Number(row.unjustifiedLeakage || 0),
      Number(row.justifiedLeakage || 0),
    ]),
  );

const formatPercent = (value = 0) => `${Number(value || 0).toFixed(Number(value || 0) % 1 === 0 ? 0 : 1)}%`;

const buildRfqDetailsUrl = (rfqId) => (
  rfqId === undefined || rfqId === null || rfqId === ""
    ? ""
    : `/rfqs/${encodeURIComponent(rfqId)}/Overview`
);

const buildMonthlyBarTooltip = (label, amount, projects = []) => {
  const projectLines = projects.length
    ? projects.map((project) => `- ${project.projectName}: ${formatCurrency(project.amount)}`)
    : ["- No project data"];

  return [label, formatCurrency(amount), "Projects:", ...projectLines].join("\n");
};

const mergeProjectAmounts = (primaryProjects = [], secondaryProjects = []) => {
  const projectMap = new Map();

  [...primaryProjects, ...secondaryProjects].forEach((project) => {
    const key = project.projectId || project.projectName || "Unassigned project";
    const current = projectMap.get(key) || {
      projectId: project.projectId || "",
      projectName: project.projectName || "Unassigned project",
      amount: 0,
    };

    current.amount += Number(project.amount || 0);
    projectMap.set(key, current);
  });

  return Array.from(projectMap.values())
    .filter((project) => project.amount > 0)
    .sort((first, second) => second.amount - first.amount)
    .slice(0, 4);
};

const normalizeLeakageForCurrentPlatform = (leakage = {}) => {
  const total = Number(leakage.total || 0);

  return {
    ...leakage,
    justifiedTotal: total,
    unjustifiedTotal: 0,
    breakdown: [
      {
        key: "JUSTIFIED_VARIANCE",
        label: "Justified variance",
        amount: total,
        pct: total > 0 ? 100 : 0,
        color: "#FD6600",
      },
    ],
    topLeakages: (leakage.topLeakages || []).map((row) => ({
      ...row,
      isJustified: true,
      justificationReasons: Array.isArray(row.justificationReasons) ? row.justificationReasons : [],
      justificationLabel: row.justificationLabel || row.reason || "",
      bucket: "JUSTIFIED_VARIANCE",
    })),
  };
};

const getLeakageJustificationReasons = (row = {}) => {
  const reasons = Array.isArray(row.justificationReasons)
    ? row.justificationReasons.map((reason) => String(reason || "").trim()).filter(Boolean)
    : [];

  if (reasons.length) return reasons;

  const fallbackReason = String(row.justificationLabel || row.reason || "").trim();
  if (fallbackReason && fallbackReason !== "Reason classification not captured yet") {
    return fallbackReason.split(",").map((reason) => reason.trim()).filter(Boolean);
  }

  return [row.isJustified ? "Justified" : "Unjustified"];
};

const normalizeYtdLeakageRowsForCurrentPlatform = (rows = []) =>
  rows.map((row) => ({
    ...row,
    justifiedLeakage: Number(row.justifiedLeakage || 0) + Number(row.unjustifiedLeakage || 0),
    unjustifiedLeakage: 0,
    justifiedLeakageProjects: mergeProjectAmounts(row.justifiedLeakageProjects, row.unjustifiedLeakageProjects),
    unjustifiedLeakageProjects: [],
  }));

const LeakageComposition = ({ leakage, tokens }) => {
  const total = Number(leakage?.total || 0);
  const breakdown = Array.isArray(leakage?.breakdown) ? leakage.breakdown : [];
  const visibleBreakdown = breakdown.filter((row) => Number(row.amount || 0) > 0);

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: tokens.faint }}>
            Leakage composition
          </p>
          <h3 className="mt-1 text-2xl font-bold" style={{ color: tokens.danger }}>
            {formatCurrency(total)}
          </h3>
        </div>
        <Badge variant={total > 0 ? "red" : "green"} tokens={tokens}>
          <IconGlyph name={total > 0 ? "alert" : "shield"} className="h-3.5 w-3.5" color={total > 0 ? tokens.danger : tokens.success} />
          {Number(leakage?.pct || 0)}% of awarded spend
        </Badge>
      </div>

      <div className="flex h-2 overflow-hidden rounded-full" style={{ backgroundColor: tokens.card }}>
        {visibleBreakdown.length > 0 ? visibleBreakdown.map((row) => (
          <span
            key={row.key || row.label}
            title={`${row.label}: ${formatCurrency(row.amount)}`}
            style={{
              width: `${Math.max(3, row.pct || 0)}%`,
              backgroundColor: row.color,
            }}
          />
        )) : (
          <span className="w-full" style={{ backgroundColor: tokens.successLight }} />
        )}
      </div>
    </div>
  );
};

const TopLeakageRows = ({ rows = [], tokens, onNavigate }) => {
  const totalLeakage = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  if (!rows.length) {
    return (
      <div className="rounded-xl border px-4 py-6 text-center text-xs" style={{ borderColor: tokens.line, color: tokens.muted }}>
        No non-L1 leakage found for the selected period.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border" style={{ borderColor: tokens.line }}>
      <div className="grid grid-cols-[1.2fr_0.8fr_0.85fr_1.05fr_0.95fr] gap-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide" style={{ backgroundColor: tokens.cardSoft, color: tokens.faint }}>
        <span>RFQ / Item</span>
        <span>Project</span>
        <span>Vendor</span>
        <span>Justification reason</span>
        <span className="text-right">Leakage</span>
      </div>
      <div className="dashboard-print-expand-scroll min-h-0 flex-1 overflow-y-auto">
        {rows.map((row, index) => {
          const justificationReasons = getLeakageJustificationReasons(row);

          return (
          <div
            key={`${row.rfqId}-${row.supplierId}-${row.itemName}-${index}`}
            className="grid grid-cols-[1.2fr_0.8fr_0.85fr_1.05fr_0.95fr] gap-3 border-t px-3 py-2 text-xs"
            style={{ borderColor: tokens.line }}
          >
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => onNavigate?.(buildRfqDetailsUrl(row.rfqId))}
                className="block max-w-full truncate text-left font-semibold hover:underline"
                style={{ color: tokens.primary }}
                title={`Open RFQ #${row.rfqId} in a new tab`}
              >
                #{row.rfqId} - {row.rfqName}
              </button>
              <p className="truncate text-[11px]" style={{ color: tokens.muted }}>
                {row.itemName}
              </p>
            </div>
            <div className="min-w-0" title={[row.projectName, row.projectLocation].filter(Boolean).join(" - ")}>
              <p className="truncate font-medium" style={{ color: tokens.ink }}>
                {row.projectName || "Unassigned project"}
              </p>
              <p className="truncate text-[10px]" style={{ color: tokens.faint }}>
                {row.projectLocation || "-"}
              </p>
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium" style={{ color: tokens.ink }}>{row.supplierName}</p>
              <p className="text-[11px]" style={{ color: row.isJustified ? tokens.warn : tokens.danger }}>
                {row.isJustified ? "Justified" : "Unjustified"}
              </p>
            </div>
            <div className="flex min-w-0 flex-wrap gap-1">
              {justificationReasons.slice(0, 3).map((reason) => (
                <span
                  key={`${row.rfqId}-${row.supplierId}-${reason}`}
                  className="max-w-full truncate rounded-full px-2 py-0.5 h-fit text-[10px] font-medium"
                  style={{
                    backgroundColor: row.isJustified ? "#FFF4DE" : tokens.dangerLight,
                    color: row.isJustified ? tokens.warn : tokens.danger,
                  }}
                  title={reason}
                >
                  {reason}
                </span>
              ))}
              {justificationReasons.length > 3 ? (
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: tokens.cardSoft, color: tokens.muted }}
                  title={justificationReasons.slice(3).join(", ")}
                >
                  +{justificationReasons.length - 3} more
                </span>
              ) : null}
            </div>
            <div className="text-right">
              <strong style={{ color: row.isJustified ? tokens.warn : tokens.danger }}>
                {formatCurrency(row.amount)}
              </strong>
              <p className="mt-1 text-[10px] font-medium" style={{ color: tokens.ink }}>
                L1 {formatCurrency(row.l1Rate)} vs Selected {row.selectedRank || row.rank || "vendor"} {formatCurrency(row.awardedRate)}
              </p>
            </div>
          </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t px-3 py-2 text-xs font-semibold" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
        <span style={{ color: tokens.muted }}>Total leakage from all rows</span>
        <span style={{ color: tokens.danger }}>{formatCurrency(totalLeakage)}</span>
      </div>
    </div>
  );
};

const TopSavingsRows = ({ rows = [], tokens, onNavigate }) => {
  const totalSavings = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  if (!rows.length) {
    return (
      <div className="rounded-xl border px-4 py-6 text-center text-xs" style={{ borderColor: tokens.line, color: tokens.muted }}>
        No savings lines found for the selected period.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border" style={{ borderColor: tokens.line }}>
      <div className="grid grid-cols-[1.2fr_0.9fr_1fr_0.8fr] gap-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide" style={{ backgroundColor: tokens.cardSoft, color: tokens.faint }}>
        <span>RFQ</span>
        <span>Project</span>
        <span>Vendor</span>
        <span className="text-right">Savings</span>
      </div>
      <div className="dashboard-print-expand-scroll min-h-0 flex-1 overflow-y-auto">
        {rows.map((row) => (
          <div
            key={`${row.rfqId}-${row.supplierId}-${row.amount}`}
            className="grid grid-cols-[1.2fr_0.9fr_1fr_0.8fr] gap-3 border-t px-3 py-2 text-xs"
            style={{ borderColor: tokens.line }}
          >
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => onNavigate?.(buildRfqDetailsUrl(row.rfqId))}
                className="block max-w-full truncate text-left font-semibold hover:underline"
                style={{ color: tokens.primary }}
                title={`Open RFQ #${row.rfqId} in a new tab`}
              >
                #{row.rfqId} - {row.rfqName}
              </button>
              <p className="truncate text-[11px]" style={{ color: tokens.muted }}>
                Initial vs final quote
              </p>
            </div>
            <div className="min-w-0" title={[row.projectName, row.projectLocation].filter(Boolean).join(" - ")}>
              <p className="truncate font-medium" style={{ color: tokens.ink }}>
                {row.projectName || "Unassigned project"}
              </p>
              <p className="truncate text-[10px]" style={{ color: tokens.faint }}>
                {row.projectLocation || "-"}
              </p>
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium" style={{ color: tokens.ink }}>
                {row.supplierName}{row.rank ? ` (${row.rank})` : ""}
              </p>
            </div>
            <div className="text-right">
              <strong style={{ color: tokens.success }}>{formatCurrency(row.amount)}</strong>
              <p className="text-[10px]" style={{ color: tokens.faint }}>
                {formatCurrency(row.initialAmount)} to {formatCurrency(row.finalAmount)}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t px-3 py-2 text-xs font-semibold" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
        <span style={{ color: tokens.muted }}>Total savings from all rows</span>
        <span style={{ color: tokens.success }}>{formatCurrency(totalSavings)}</span>
      </div>
    </div>
  );
};

const SavingsLeakageChart = ({ rows = [], tokens }) => {
  const maxValue = getMaxMonthlyValue(rows);
  const chartRows = rows.length ? rows : [];

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
      <div className="dashboard-print-expand-scroll flex h-[190px] items-center gap-3 overflow-x-auto pb-2">
        {chartRows.map((row) => {
          const savingsHeight = Math.max(3, (Number(row.savings || 0) / maxValue) * 72);
          const unjustifiedHeight = Math.max(3, (Number(row.unjustifiedLeakage || 0) / maxValue) * 72);
          const justifiedHeight = Math.max(3, (Number(row.justifiedLeakage || 0) / maxValue) * 72);

          return (
            <div key={row.key || row.label} className="flex min-w-[58px] flex-col items-center">
              <div className="flex h-[154px] items-center gap-1">
                <div className="flex h-full flex-col justify-end">
                  <span
                    className="w-3 rounded-t"
                    title={buildMonthlyBarTooltip("Savings", row.savings, row.savingsProjects)}
                    style={{ height: savingsHeight, backgroundColor: tokens.success }}
                  />
                </div>
                <div className="flex h-full flex-col justify-end">
                  <span
                    className="w-3 rounded-t"
                    title={buildMonthlyBarTooltip("Unjustified leakage", row.unjustifiedLeakage, row.unjustifiedLeakageProjects)}
                    style={{ height: unjustifiedHeight, backgroundColor: tokens.danger }}
                  />
                </div>
                <div className="flex h-full flex-col justify-end">
                  <span
                    className="w-3 rounded-t"
                    title={buildMonthlyBarTooltip("Justified leakage", row.justifiedLeakage, row.justifiedLeakageProjects)}
                    style={{ height: justifiedHeight, backgroundColor: tokens.warn }}
                  />
                </div>
              </div>
              <span className="text-[10px] font-medium" style={{ color: tokens.muted }}>{row.label}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-[11px]" style={{ color: tokens.muted }}>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: tokens.success }} />Savings</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: tokens.danger }} />Unjustified leakage</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: tokens.warn }} />Justified leakage</span>
      </div>
    </div>
  );
};

const projectSortOptions = [
  { key: "highestLeakage", label: "Highest leakage" },
  { key: "highestSavings", label: "Highest savings" },
  { key: "worstLeak", label: "Worst leak %" },
  { key: "mostRfqs", label: "Most RFQs" },
];

const sortProjectRows = (rows = [], sortKey = "highestLeakage") => {
  const sorters = {
    highestLeakage: (first, second) => second.leakage - first.leakage || second.rfqCount - first.rfqCount,
    highestSavings: (first, second) => second.savings - first.savings || second.leakage - first.leakage,
    worstLeak: (first, second) => second.leakagePercent - first.leakagePercent || second.leakage - first.leakage,
    mostRfqs: (first, second) => second.rfqCount - first.rfqCount || second.leakage - first.leakage,
  };

  return [...rows].sort(sorters[sortKey] || sorters.highestLeakage);
};

const ProjectLeakageSummaryCard = ({ label, value, tone = "neutral", tokens }) => {
  const colorMap = {
    danger: tokens.danger,
    success: tokens.success,
    warn: tokens.warn,
    neutral: tokens.ink,
  };

  return (
    <div className="rounded-xl border px-4 py-3" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
      <p className="text-xl font-bold" style={{ color: colorMap[tone] || tokens.ink }}>{value}</p>
      <p className="mt-1 text-[11px] font-semibold" style={{ color: tokens.muted }}>{label}</p>
    </div>
  );
};

const EmptyProjectLeakage = ({ tokens }) => (
  <div className="rounded-xl border px-4 py-8 text-center" style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}>
    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: tokens.successLight }}>
      <IconGlyph name="shield" className="h-5 w-5" color={tokens.success} />
    </div>
    <p className="text-sm font-semibold" style={{ color: tokens.ink }}>No project leakage found</p>
    <p className="mt-1 text-xs" style={{ color: tokens.muted }}>Closed RFQs for this period are either L1-aligned or do not have comparable project leakage.</p>
  </div>
);

const ProjectLeakageTable = ({ projectLeakage = {}, tokens, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("highestLeakage");
  const rows = useMemo(
    () => (Array.isArray(projectLeakage.rows) ? projectLeakage.rows : []),
    [projectLeakage.rows],
  );
  const summary = projectLeakage.summary || {};

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchingRows = normalizedSearch
      ? rows.filter((row) => [row.projectName, row.location].some((value) =>
          String(value || "").toLowerCase().includes(normalizedSearch)
        ))
      : rows;

    return sortProjectRows(matchingRows, sortKey);
  }, [rows, searchTerm, sortKey]);

  return (
    <Card tokens={tokens}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="text-[13px] font-semibold" style={{ color: tokens.ink }}>All projects - leakage detail</span>
          <p className="text-[11px]" style={{ color: tokens.faint }}>
            Project-wise leakage and savings from closed RFQs in the selected period.
          </p>
        </div>
        <Badge variant="gray" tokens={tokens}>{summary.totalProjects || rows.length} projects</Badge>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
        <ProjectLeakageSummaryCard label="Total leakage - all projects" value={formatCurrency(summary.totalLeakage)} tone="danger" tokens={tokens} />
        <ProjectLeakageSummaryCard label="Total savings - all projects" value={formatCurrency(summary.totalSavings)} tone="success" tokens={tokens} />
        <ProjectLeakageSummaryCard label="Projects with leakage" value={summary.projectsWithLeakage || 0} tokens={tokens} />
        <ProjectLeakageSummaryCard label="Avg leakage % of savings" value={formatPercent(summary.avgLeakagePercentOfSavings)} tone="warn" tokens={tokens} />
      </div>

      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search project name"
          className="h-9 rounded-lg border px-3 text-xs outline-none"
          style={{ borderColor: tokens.line, backgroundColor: tokens.card, color: tokens.ink }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: tokens.faint }}>
            Sort by:
          </span>
          {projectSortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSortKey(option.key)}
              className="rounded-lg border px-3 py-2 text-[11px] font-semibold transition"
              style={{
                borderColor: sortKey === option.key ? tokens.brand : tokens.line,
                backgroundColor: sortKey === option.key ? "#FFF0E6" : tokens.cardSoft,
                color: sortKey === option.key ? tokens.brand : tokens.muted,
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {!filteredRows.length ? (
        <EmptyProjectLeakage tokens={tokens} />
      ) : (
        <div className="overflow-hidden rounded-xl border" style={{ borderColor: tokens.line }}>
          <div className="grid grid-cols-[44px_1.5fr_0.6fr_0.8fr_0.8fr_0.7fr_1fr] gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: tokens.cardSoft, color: tokens.faint }}>
            <span>#</span>
            <span>Project</span>
            <span className="text-right">RFQs</span>
            <span className="text-right">Savings</span>
            <span className="text-right">Leakage</span>
            <span className="text-right">Leak %</span>
            <span>Leakage bar</span>
          </div>
          <div className="dashboard-print-expand-scroll max-h-[360px] overflow-auto">
            {filteredRows.map((row, index) => {
              const projectTargetUrl = row.projectId
                ? `/rfqs/workspace/my-workspace?status=CLOSED&projectId=${encodeURIComponent(row.projectId)}`
                : "";

              return (
              <div
                key={`${row.projectId || row.projectName}-${index}`}
                className="grid grid-cols-[44px_1.5fr_0.6fr_0.8fr_0.8fr_0.7fr_1fr] items-center gap-2 border-t px-3 py-2 text-xs"
                style={{ borderColor: tokens.line }}
              >
                <span style={{ color: tokens.faint }}>{index + 1}</span>
                <div className="min-w-0">
                  <p className="truncate font-semibold" style={{ color: tokens.ink }}>{row.projectName || "Unassigned project"}</p>
                  <p className="truncate text-[10px]" style={{ color: tokens.faint }}>{row.location || "-"}</p>
                </div>
                <span className="text-right font-semibold">
                  {projectTargetUrl ? (
                    <button
                      type="button"
                      onClick={() => onNavigate?.(projectTargetUrl)}
                      className="font-semibold hover:underline"
                      style={{ color: tokens.primary }}
                      title={`View RFQs for ${row.projectName}`}
                    >
                      {row.rfqCount || 0}
                    </button>
                  ) : (
                    <span style={{ color: tokens.ink }}>{row.rfqCount || 0}</span>
                  )}
                </span>
                <span className="text-right font-semibold" style={{ color: tokens.success }}>{formatCurrency(row.savings)}</span>
                <span className="text-right font-semibold" style={{ color: row.leakage > 0 ? tokens.danger : tokens.muted }}>{formatCurrency(row.leakage)}</span>
                <span className="text-right">
                  <span
                    className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{
                      backgroundColor: row.leakagePercent > 0 ? tokens.dangerLight : tokens.successLight,
                      color: row.leakagePercent > 0 ? tokens.danger : tokens.success,
                    }}
                  >
                    {formatPercent(row.leakagePercent)}
                  </span>
                </span>
                <div className="h-2 overflow-hidden rounded-full" style={{ backgroundColor: tokens.line }}>
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${Math.max(row.leakage > 0 ? 4 : 0, row.leakageBarPercent || 0)}%`,
                      backgroundColor: row.leakage > 0 ? tokens.danger : tokens.success,
                    }}
                  />
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

const LeakageAnalysis = ({ stats, tokens, onNavigate }) => {
  const leakage = normalizeLeakageForCurrentPlatform(stats.leakage || {});
  const leakageYtd = normalizeYtdLeakageRowsForCurrentPlatform(stats.leakageYtd || []);
  const projectLeakage = stats.projectLeakage || {};
  const selectedPeriod = stats.filters?.period;
  const trendPeriodLabel = selectedPeriod?.isFiltered ? selectedPeriod.label : "YTD";

  return (
    <>
      <SectionLabel tokens={tokens}>Leakage analysis</SectionLabel>
      <Card tokens={tokens}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <span className="text-[13px] font-semibold" style={{ color: tokens.ink }}>Leakage breakdown</span>
            <p className="text-[11px]" style={{ color: tokens.faint }}>
              Non-L1 awarded value compared item/scope-wise against lowest valid quotes.
            </p>
          </div>
          <Badge variant="gray" tokens={tokens}>
            Closed RFQs only
          </Badge>
        </div>
        <LeakageComposition leakage={leakage} tokens={tokens} />
      </Card>

      <Card tokens={tokens} className="mt-3 flex h-[520px] flex-col">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold" style={{ color: tokens.ink }}>Largest leakage lines</span>
          <span className="text-[11px]" style={{ color: tokens.faint }}>
            {leakage.topLeakages?.length || 0} rows
          </span>
        </div>
        <TopLeakageRows rows={leakage.topLeakages || []} tokens={tokens} onNavigate={onNavigate} />
      </Card>

      <Card tokens={tokens} className="mt-3">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <span className="text-[13px] font-semibold" style={{ color: tokens.ink }}>
              Savings vs Leakage - {trendPeriodLabel}
            </span>
            <p className="text-[11px]" style={{ color: tokens.faint }}>
              Monthly view of negotiated savings against currently justified leakage.
            </p>
          </div>
          <span className="text-[11px] font-semibold" style={{ color: tokens.brand }}>
            {new Date().getFullYear()}
          </span>
        </div>
        <SavingsLeakageChart rows={leakageYtd} tokens={tokens} />
      </Card>

      <Card tokens={tokens} className="mt-3 flex h-[520px] flex-col">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold" style={{ color: tokens.ink }}>Saving lines</span>
          <span className="text-[11px]" style={{ color: tokens.faint }}>
            {leakage.topSavings?.length || 0} rows
          </span>
        </div>
        <TopSavingsRows rows={leakage.topSavings || []} tokens={tokens} onNavigate={onNavigate} />
      </Card>
      <div className="mt-3">
        <ProjectLeakageTable projectLeakage={projectLeakage} tokens={tokens} onNavigate={onNavigate} />
      </div>
    </>
  );
};

export default LeakageAnalysis;
